import { CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { migrateAssignments } from "~/api/v2/requests";
import { useExistingAssignments } from "~/api/v2/useExistingAssignments";
import { Alert, AlertDescription } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";

const LEGACY_MIGRATION_STORAGE_KEY = "nh-legacy-migration-completed";

export function isLegacyMigrationCompleted(): boolean {
  return sessionStorage.getItem(LEGACY_MIGRATION_STORAGE_KEY) === "true";
}

export function setLegacyMigrationCompleted(): void {
  sessionStorage.setItem(LEGACY_MIGRATION_STORAGE_KEY, "true");
}

interface LegacyMigrationCardProps {
  setupToken: string;
  onSkip: () => void;
}

type MigrateResult =
  | { status: "idle" }
  | { status: "migrating" }
  | { status: "complete"; migrated: number; failed: number }
  | { status: "error" };

export function LegacyMigrationCard({
  setupToken,
  onSkip,
}: LegacyMigrationCardProps) {
  const { t } = useTranslation();

  const {
    data: assignments,
    isLoading,
    error: fetchError,
  } = useExistingAssignments(setupToken);

  const [migrateResult, setMigrateResult] = useState<MigrateResult>(() => {
    return isLegacyMigrationCompleted()
      ? { status: "complete", migrated: 0, failed: 0 }
      : { status: "idle" };
  });

  const handleMigrate = async () => {
    setMigrateResult({ status: "migrating" });

    try {
      const result = await migrateAssignments(setupToken);
      setMigrateResult({
        status: "complete",
        migrated: result.migrated,
        failed: result.failed,
      });
      setLegacyMigrationCompleted();
    } catch {
      setMigrateResult({ status: "error" });
    }
  };

  const handleSkip = () => {
    setLegacyMigrationCompleted();
    onSkip();
  };

  const isComplete = migrateResult.status === "complete";
  const hasAssignments = assignments && assignments.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("msIntegration.steps.userMigration.title")}</CardTitle>
        <CardDescription>
          {t("msIntegration.steps.userMigration.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="text-primary h-4 w-4 animate-spin" />
            <span className="text-muted-foreground text-sm">
              {t("msIntegration.status.userAssignment.loadingAssignments")}
            </span>
          </div>
        )}

        {fetchError && (
          <Alert variant="destructive">
            <AlertDescription>
              {t("msIntegration.status.userAssignment.loadError")}
            </AlertDescription>
          </Alert>
        )}

        {assignments?.length === 0 && (
          <>
            <p className="text-muted-foreground text-sm">
              {t("msIntegration.status.userAssignment.noAssignments")}
            </p>
            <Button variant="outline" onClick={handleSkip} className="w-full">
              {t("msIntegration.steps.userMigration.skipButton")}
            </Button>
          </>
        )}

        {hasAssignments && !isComplete && (
          <>
            <p className="text-sm">
              {t("msIntegration.status.userAssignment.assignmentsFound", {
                count: assignments.length,
              })}
            </p>
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {assignments.map((a) => (
                <div
                  key={a.id ?? a.principalId}
                  className="text-muted-foreground flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <span>{a.principalDisplayName ?? a.principalId}</span>
                  <span className="text-xs opacity-70">
                    {a.principalType === "User"
                      ? t("msIntegration.status.userAssignment.user")
                      : a.principalType === "Group"
                        ? t("msIntegration.status.userAssignment.group")
                        : a.principalType}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleMigrate}
                disabled={migrateResult.status === "migrating"}
                className="flex-1"
              >
                {migrateResult.status === "migrating" ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("msIntegration.status.userAssignment.migrating")}
                  </span>
                ) : (
                  t("msIntegration.status.userAssignment.migrateButton", {
                    count: assignments.length,
                  })
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={migrateResult.status === "migrating"}
              >
                {t("msIntegration.steps.userMigration.skipButton")}
              </Button>
            </div>
          </>
        )}

        {isComplete && (
          <>
            <Alert variant={migrateResult.failed > 0 ? "warn" : "default"}>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                {migrateResult.failed > 0
                  ? t("msIntegration.status.userAssignment.migratePartial", {
                      migrated: migrateResult.migrated,
                      failed: migrateResult.failed,
                    })
                  : t("msIntegration.status.userAssignment.migrateSuccess", {
                      count: migrateResult.migrated,
                    })}
              </AlertDescription>
            </Alert>
            <Button onClick={handleSkip} className="w-full">
              {t("commons.buttons.proceed")}
            </Button>
          </>
        )}

        {migrateResult.status === "error" && (
          <Alert variant="destructive">
            <AlertDescription>
              {t("msIntegration.status.userAssignment.migrateError")}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
